import Card from '../../components/card/Card';
import { balanceInfo, clientData, riskData } from '../../types/interfaces';
import { useClientTheme } from '../../context/ThemeContext';
import {
  RISK_CODES,
  APPLICATION_STATUS,
  initialClientData,
} from '../../utils/Constants';
import CheckLightResume from './CheckLight/ChecklightResume';
import { useRef, useState, useEffect } from 'react';
import Modal from '../../components/modal/modal';
import { getProductLines } from '../../services/ClientService';
import TextArea from '../../components/textarea/TextArea';
import { updateApplication } from '../../services/ApplicationService';
import { useNavigate } from 'react-router-dom';
import { Toast, useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';
import Button from '../../components/button/Button';
import P from '../../components/texts/p/P';
import { routes } from '../../routes/routePaths';
import useStatus from '../../hooks/useStatus';
import { hasError, isSuccessfulResponse } from '../../utils/Utils';
import H from '../../components/texts/h/H';
import SummaryAmounts from '../common/SummaryAmounts';
import { useRiskPdf } from '../../hooks/useRiskPdf';
import RiskAnalysisPdf from './Pdf/RiskAnalysisPdf';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { useOfficers } from '../../hooks/useOfficers';

interface CheckLightProps {
  riskData: riskData | undefined;
  isLoading: boolean;
  handleRiskData: Function;
  handleRiskDataAndDebts?: Function;
  cuit: string | undefined;
  applicationId: string | undefined;
  files: any;
  legalEntity: any;
  applicationStatus: number;
  debts: any;
  declineOrChangeBtn?: boolean;
  hideInfo?: boolean;
  balanceData?: balanceInfo;
  postBalanceData?: any;
  clientData?: clientData;
  showAmounts?: boolean;
}

const RiskResume: React.FC<CheckLightProps> = ({
  riskData,
  isLoading,
  handleRiskData,
  handleRiskDataAndDebts,
  cuit,
  applicationId,
  files,
  applicationStatus,
  debts,
  declineOrChangeBtn = true,
  hideInfo = true,
  balanceData,
  postBalanceData,
  clientData,
  showAmounts = true,
}) => {
  const { clientTheme } = useClientTheme();
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const navigate = useNavigate();
  const [isSendingToCommittee, setIsSendingToCommittee] = useState(false);
  const [openCommitteeModal, setOpenCommitteeModal] = useState(false);
  const { isInCommittee } = useStatus({ applicationStatus });
  const [clientInfo, setClientInfo] = useState<clientData>(
    clientData || initialClientData
  );
  const pdfRef = useRef<HTMLDivElement>(null);
  const { isGenerating, generatePdfFromNode } = useRiskPdf({
    fileName: `analisis_riesgo_${cuit || 'cliente'}.pdf`,
  });
  const [productLines, setProductLines] = useState<Array<{ code: string; label: string }>>([]);

  // Fetch product lines for the PDF
  useEffect(() => {
    getProductLines()
      .then((response) => {
        if (response && 'data' in response) {
          setProductLines(response.data);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch product lines:', error);
      });
  }, []);

  const handleToSendToCommittee = async (status: string) => {
    setIsSendingToCommittee(true);
    await updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: 'Enviaste la solicitud. El comité será notificado.',
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error inesperado. Intentá nuevamente.',
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: false,
          autoClose: 5000,
        });
      }
      setIsSendingToCommittee(false);
      setOpenCommitteeModal(false);
    });
  };

  const handleDownloadPdf = async () => {
    await generatePdfFromNode(pdfRef.current);
  };

  const sendToCommitteeModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Al enviar la solicitud, esta pasará al comité que tomará la decisión
          de aprobarla o no.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí un comentario sobre la solicitud o dejalo en blanco.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const renderProductsAndRequestAmount = (clientData: clientData) => {
    return (
      <Card margin={{ bottom: '5' }}>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
          <H
            type='4'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Solicitud
          </H>
        </Card>
        <Card display='flex' gap='4px'>
          <P type='2' variant='neutral' weight='bold'>
            Motivo:
          </P>
          <P type='2' variant='neutral'>
            {clientInfo.reason}
          </P>
        </Card>
        <SummaryAmounts
          clientData={clientData}
          isManualAmount={clientData.manual_total_amount}
          products={clientData.products}
          amounts={clientData.requested_amount}
          hideFianzaPagare={hideInfo}
        />
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };

  return (
    <Card spacing={{ left: '6', top: '4' }}>
      <RiskAnalysisPdf
        ref={pdfRef}
        clientData={clientInfo}
        riskData={riskData}
        balanceData={balanceData}
        postBalanceData={postBalanceData}
        debts={debts}
        documents={files}
        cuit={cuit}
        referrerNames={referrerNames}
        officers={officers}
        productLines={productLines}
      />
      <Toast id='application-toast' />
      <Modal
        isOpen={openCommitteeModal}
        onClose={() => setOpenCommitteeModal(false)}
        title={'Enviar solicitud a comité'}
        primaryText='Enviar a comité'
        primaryVariant='primary'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setOpenCommitteeModal(false);
        }}
        onPrimaryAction={() => {
          handleToSendToCommittee(APPLICATION_STATUS.committee.label);
        }}
        loadingPrimaryBtn={isSendingToCommittee}
        children={sendToCommitteeModal()}
      />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />

      {showAmounts && !hideInfo && renderProductsAndRequestAmount(clientInfo)}

      <CheckLightResume
        data={riskData}
        showBalanceInfo={true}
        isLoading={isLoading}
        handleRiskData={handleRiskData}
        handleRiskDataAndDebts={handleRiskDataAndDebts}
        riskCodes={[RISK_CODES.bpb, RISK_CODES.casfog, RISK_CODES.bcra]}
        debts={debts}
        documents={files}
        cuit={cuit}
        hideInfo={hideInfo}
        balanceData={balanceData}
        postBalanceData={postBalanceData}
        onDownloadPdf={handleDownloadPdf}
        isPdfLoading={isGenerating}
      />
      <Card
        display='flex'
        justifyContent='space-between'
        margin={{ top: '5' }}
        gap='16px'
      >
        {declineOrChangeBtn && (
          <Card width='280px'>
            <Button
              id='checkRisk-button-reject'
              size='lg'
              variant='secondary'
              onClick={() => setIsRejectApplication(true)}
              fullWidth
              disabled={isSendingApplication}
            >
              {isSendingApplication ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Declinar o solicitar cambios'
              )}
            </Button>
          </Card>
        )}
        {!isInCommittee && !hideInfo && (
          <Card width='200px'>
            <Button
              id='checkRisk-button-send-to-committee'
              size='lg'
              variant='primary'
              fullWidth
              onClick={() => setOpenCommitteeModal(true)}
            >
              Enviar a comité
            </Button>
          </Card>
        )}
      </Card>
    </Card>
  );
};

export default RiskResume;
